import { ChevronDown, ChevronUp } from "lucide-react";
import { forwardRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Empty } from "./Empty";
import { Spin } from "./Spin";

export interface ColumnType<T> {
	title: React.ReactNode;
	dataIndex?: string;
	key: string;
	width?: number | string;
	ellipsis?: boolean;
	align?: "left" | "center" | "right";
	render?: (value: any, record: T, index: number) => React.ReactNode;
	sorter?: boolean | ((a: T, b: T) => number);
	fixed?: "left" | "right";
}

export interface TableProps<T> {
	columns: ColumnType<T>[];
	dataSource: T[];
	rowKey: string | ((record: T) => string);
	loading?: boolean;
	pagination?:
		| false
		| {
				current?: number;
				pageSize?: number;
				total?: number;
				onChange?: (page: number, pageSize: number) => void;
				showSizeChanger?: boolean;
				showTotal?: (total: number, range: [number, number]) => React.ReactNode;
		  };
	size?: "small" | "middle" | "large";
	bordered?: boolean;
	scroll?: { x?: number | string; y?: number | string };
	rowSelection?: {
		selectedRowKeys?: string[];
		onChange?: (selectedRowKeys: string[], selectedRows: T[]) => void;
	};
	onRow?: (record: T, index: number) => React.HTMLAttributes<HTMLTableRowElement>;
	locale?: { emptyText?: React.ReactNode };
	className?: string;
}

function TableInner<T>(
	{
		columns,
		dataSource,
		rowKey,
		loading = false,
		pagination = false,
		size = "middle",
		bordered = false,
		scroll,
		rowSelection,
		onRow,
		locale,
		className,
	}: TableProps<T>,
	ref: React.ForwardedRef<HTMLDivElement>,
) {
	const [sortConfig, setSortConfig] = useState<{
		key: string;
		direction: "asc" | "desc";
	} | null>(null);

	const getRowKey = (record: T, index: number): string => {
		if (typeof rowKey === "function") {
			return rowKey(record);
		}
		return String((record as Record<string, unknown>)[rowKey] ?? index);
	};

	const getValue = (record: T, dataIndex?: string): any => {
		if (!dataIndex) return undefined;
		const keys = dataIndex.split(".");
		let value: any = record;
		for (const key of keys) {
			value = value?.[key];
		}
		return value;
	};

	// 排序数据
	const sortedData = [...dataSource];
	if (sortConfig) {
		const column = columns.find((c) => c.key === sortConfig.key);
		if (column?.sorter) {
			sortedData.sort((a, b) => {
				let result = 0;
				if (typeof column.sorter === "function") {
					result = column.sorter(a, b);
				} else {
					const aVal = getValue(a, column.dataIndex);
					const bVal = getValue(b, column.dataIndex);
					if (aVal < bVal) result = -1;
					if (aVal > bVal) result = 1;
				}
				return sortConfig.direction === "desc" ? -result : result;
			});
		}
	}

	// 分页
	const paginationConfig = pagination || {
		current: 1,
		pageSize: dataSource.length,
	};
	const { current = 1, pageSize = 10 } = paginationConfig;
	const startIndex = (current - 1) * pageSize;
	const endIndex = startIndex + pageSize;
	const paginatedData = pagination ? sortedData.slice(startIndex, endIndex) : sortedData;
	const total = pagination ? (pagination.total ?? dataSource.length) : dataSource.length;

	const handleSort = (column: ColumnType<T>) => {
		if (!column.sorter) return;

		if (sortConfig?.key === column.key) {
			if (sortConfig.direction === "asc") {
				setSortConfig({ key: column.key, direction: "desc" });
			} else {
				setSortConfig(null);
			}
		} else {
			setSortConfig({ key: column.key, direction: "asc" });
		}
	};

	const sizeStyles = {
		small: "text-xs [&_th]:px-2 [&_th]:py-2 [&_td]:px-2 [&_td]:py-1.5",
		middle: "text-sm [&_th]:px-4 [&_th]:py-3 [&_td]:px-4 [&_td]:py-2.5",
		large: "text-base [&_th]:px-6 [&_th]:py-4 [&_td]:px-6 [&_td]:py-3",
	};

	const renderPagination = () => {
		if (!pagination) return null;

		const totalPages = Math.ceil(total / pageSize);
		const { onChange, showTotal } = pagination;

		return (
			<div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
				{showTotal && (
					<span className="text-sm text-gray-500">
						{showTotal(total, [startIndex + 1, Math.min(endIndex, total)])}
					</span>
				)}
				<div className="flex items-center gap-1 ml-auto">
					<button
						type="button"
						onClick={() => onChange?.(current - 1, pageSize)}
						disabled={current <= 1}
						className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:bg-gray-700"
					>
						上一页
					</button>
					<span className="px-3 py-1 text-sm">
						{current} / {totalPages}
					</span>
					<button
						type="button"
						onClick={() => onChange?.(current + 1, pageSize)}
						disabled={current >= totalPages}
						className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:hover:bg-gray-700"
					>
						下一页
					</button>
				</div>
			</div>
		);
	};

	return (
		<div
			ref={ref}
			className={cn(
				"bg-white dark:bg-gray-800 rounded-lg overflow-hidden",
				bordered && "border border-gray-200 dark:border-gray-700",
				className,
			)}
		>
			<div
				className="overflow-auto"
				style={{
					maxHeight: scroll?.y,
					overflowX: scroll?.x ? "auto" : undefined,
				}}
			>
				<table
					className={cn("w-full border-collapse", sizeStyles[size])}
					style={{ minWidth: scroll?.x }}
				>
					<thead className="bg-gray-50 dark:bg-gray-900/50 sticky top-0">
						<tr>
							{rowSelection && (
								<th className="w-12 text-center">
									<input
										type="checkbox"
										className="rounded border-gray-300 dark:border-gray-600"
										checked={rowSelection.selectedRowKeys?.length === dataSource.length}
										onChange={(e) => {
											if (e.target.checked) {
												rowSelection.onChange?.(
													dataSource.map((r, i) => getRowKey(r, i)),
													dataSource,
												);
											} else {
												rowSelection.onChange?.([], []);
											}
										}}
									/>
								</th>
							)}
							{columns.map((column) => (
								<th
									key={column.key}
									className={cn(
										"font-medium text-gray-700 dark:text-gray-300 text-left border-b border-gray-200 dark:border-gray-700",
										column.align === "center" && "text-center",
										column.align === "right" && "text-right",
										column.sorter &&
											"cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-800",
									)}
									style={{ width: column.width }}
									onClick={() => handleSort(column)}
								>
									<div className="flex items-center gap-1">
										{column.title}
										{column.sorter && (
											<span className="flex flex-col">
												<ChevronUp
													className={cn(
														"h-3 w-3 -mb-1",
														sortConfig?.key === column.key && sortConfig.direction === "asc"
															? "text-primary-500"
															: "text-gray-300",
													)}
												/>
												<ChevronDown
													className={cn(
														"h-3 w-3",
														sortConfig?.key === column.key && sortConfig.direction === "desc"
															? "text-primary-500"
															: "text-gray-300",
													)}
												/>
											</span>
										)}
									</div>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{loading ? (
							<tr>
								<td colSpan={columns.length + (rowSelection ? 1 : 0)} className="py-12">
									<Spin tip="加载中..." />
								</td>
							</tr>
						) : paginatedData.length === 0 ? (
							<tr>
								<td colSpan={columns.length + (rowSelection ? 1 : 0)} className="py-12">
									<Empty description={locale?.emptyText} />
								</td>
							</tr>
						) : (
							paginatedData.map((record, index) => {
								const key = getRowKey(record, startIndex + index);
								const rowProps = onRow?.(record, startIndex + index) || {};

								return (
									<tr
										key={key}
										className={cn(
											"border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors",
											rowSelection?.selectedRowKeys?.includes(key) &&
												"bg-primary-50 dark:bg-primary-900/20",
										)}
										{...rowProps}
									>
										{rowSelection && (
											<td className="text-center">
												<input
													type="checkbox"
													className="rounded border-gray-300 dark:border-gray-600"
													checked={rowSelection.selectedRowKeys?.includes(key)}
													onChange={(e) => {
														const newKeys = e.target.checked
															? [...(rowSelection.selectedRowKeys || []), key]
															: rowSelection.selectedRowKeys?.filter((k) => k !== key) || [];
														const newRows = dataSource.filter((r, i) =>
															newKeys.includes(getRowKey(r, i)),
														);
														rowSelection.onChange?.(newKeys, newRows);
													}}
												/>
											</td>
										)}
										{columns.map((column) => {
											const value = getValue(record, column.dataIndex);
											const content = column.render
												? column.render(value, record, startIndex + index)
												: value;

											return (
												<td
													key={column.key}
													className={cn(
														"text-gray-700 dark:text-gray-200",
														column.align === "center" && "text-center",
														column.align === "right" && "text-right",
														column.ellipsis && "truncate max-w-0",
													)}
													style={{ width: column.width }}
												>
													{content}
												</td>
											);
										})}
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>
			{renderPagination()}
		</div>
	);
}

const Table = forwardRef(TableInner) as <T>(
	props: TableProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof TableInner>;

export { Table };
